import { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  Plus, Search, Box, Monitor, MoreHorizontal, Pencil, Trash2, ChevronRight, ChevronLeft,
} from 'lucide-react'
import { Button } from '@meno/ui/components/ui/button'
import { Input } from '@meno/ui/components/ui/input'
import { Skeleton } from '@meno/ui/components/ui/skeleton'
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from '@meno/ui/components/ui/dropdown-menu'
import { ConfirmDialog } from '@meno/ui/components/ui/confirm-dialog'
import {
  getScreenGroups, createScreenGroup, updateScreenGroup, deleteScreenGroup,
} from '@meno/ui/lib/api'
import ScreenGroupDialog, { type GroupScreen } from './ScreenGroupDialog'

// A screen group as returned by /screen-groups — screens plus the derived
// online/total counts used for the health bar.
type ScreenGroupRow = {
  id: number
  name: string
  description?: string
  screens: { id: number; name: string; status?: string }[]
  online_count: number
  total_count: number
  created_at: string
}

// ScreenGroupsPanel — the full groups management surface (search + grid +
// create/edit/delete), extracted so both the Dashboard's "My Groups" tab and
// the Screens page's Groups subpage render the same thing. It fetches its own
// groups but takes the screen list from the parent (which already has it) so
// the picker doesn't re-fetch.
export default function ScreenGroupsPanel({ allScreens }: { allScreens: GroupScreen[] }) {
  const { t } = useTranslation()

  const [groups, setGroups] = useState<ScreenGroupRow[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [createOpen, setCreateOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<ScreenGroupRow | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<ScreenGroupRow | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await getScreenGroups()
      setGroups(data)
    } catch {
      toast.error(t('group.loadFailed', 'Failed to load groups'))
    } finally {
      setLoading(false)
    }
  }, [t])
  useEffect(() => { load() }, [load])

  const handleCreate = async (data: { name: string; description: string; screen_ids: number[] }) => {
    try {
      const { data: created } = await createScreenGroup({
        name: data.name,
        description: data.description || undefined,
        screen_ids: data.screen_ids,
      })
      setGroups(prev => [created, ...prev])
      setCreateOpen(false)
      toast.success(t('group.created', 'Group created'))
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || t('group.createFailed', 'Failed to create group'))
      throw err
    }
  }

  const handleEdit = async (data: { name: string; description: string; screen_ids: number[] }) => {
    if (!editTarget) return
    try {
      const { data: updated } = await updateScreenGroup(editTarget.id, {
        name: data.name,
        description: data.description || undefined,
        screen_ids: data.screen_ids,
      })
      setGroups(prev => prev.map(g => g.id === updated.id ? updated : g))
      setEditTarget(null)
      toast.success(t('common.saved', 'Saved'))
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || t('common.updateFailed', 'Failed to update'))
      throw err
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    try {
      await deleteScreenGroup(deleteTarget.id)
      setGroups(prev => prev.filter(g => g.id !== deleteTarget.id))
      toast.success(t('common.deleted', 'Deleted'))
    } catch {
      toast.error(t('common.updateFailed', 'Failed to update'))
    } finally {
      setDeleteTarget(null)
    }
  }

  const filtered = search.trim()
    ? groups.filter(g => g.name.toLowerCase().includes(search.toLowerCase()))
    : groups

  return (
    <div className="space-y-4">
      {/* Toolbar — search left, New Group right. */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="relative flex-1 min-w-0 max-w-md">
          <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t('dashboard.searchGroups', 'Search groups…')}
            className="ps-9 h-10"
          />
        </div>
        <Button onClick={() => setCreateOpen(true)} className="gap-2 h-10 shrink-0">
          <Plus className="w-4 h-4" />
          {t('dashboard.newGroup', 'New Group')}
        </Button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)}
        </div>
      ) : filtered.length === 0 ? (
        <GroupsEmptyState onCreate={() => setCreateOpen(true)} hasSearch={!!search.trim()} />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map(g => (
            <GroupCard
              key={g.id}
              group={g}
              onOpen={() => setEditTarget(g)}
              onEdit={() => setEditTarget(g)}
              onDelete={() => setDeleteTarget(g)}
            />
          ))}
        </div>
      )}

      {createOpen && (
        <ScreenGroupDialog
          group={null}
          allScreens={allScreens}
          onSave={handleCreate}
          onClose={() => setCreateOpen(false)}
        />
      )}
      {editTarget && (
        <ScreenGroupDialog
          group={editTarget}
          allScreens={allScreens}
          onSave={handleEdit}
          onClose={() => setEditTarget(null)}
        />
      )}
      <ConfirmDialog
        open={!!deleteTarget}
        title={t('group.deleteTitle', 'Delete Group')}
        description={t('group.deleteDesc', 'This group will be removed. The screens it contains are not deleted.')}
        onConfirm={handleDelete}
        onClose={() => setDeleteTarget(null)}
      />
    </div>
  )
}

// ── GroupCard ────────────────────────────────────────────────────────────
function GroupCard({
  group, onOpen, onEdit, onDelete,
}: {
  group: ScreenGroupRow
  onOpen: () => void
  onEdit: () => void
  onDelete: () => void
}) {
  const { t, i18n } = useTranslation()
  const ratio = group.total_count > 0
    ? Math.round((group.online_count / group.total_count) * 100)
    : 0
  const Chevron = ChevronRight
  const stop = (fn: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    fn()
  }
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') onOpen() }}
      className="group relative rounded-xl border border-[hsl(var(--border-card,var(--border)))] bg-card hover:border-primary/30 transition-all p-4 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/30"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center shrink-0">
          <Box className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold truncate">{group.name}</p>
          {group.description && (
            <p className="text-[11px] text-muted-foreground truncate">{group.description}</p>
          )}
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger
            aria-label={t('common.more', 'More')}
            onClick={e => e.stopPropagation()}
            className="w-7 h-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors shrink-0"
          >
            <MoreHorizontal className="w-4 h-4" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-40" onClick={e => e.stopPropagation()}>
            <DropdownMenuItem onClick={stop(onEdit)} className="gap-2">
              <Pencil className="w-4 h-4" />
              {t('common.edit', 'Edit')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={stop(onDelete)} className="gap-2 text-destructive focus:text-destructive">
              <Trash2 className="w-4 h-4" />
              {t('common.delete', 'Delete')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="mt-3 flex items-center justify-between text-[11px]">
        <span className="flex items-center gap-1.5 text-muted-foreground">
          <Monitor className="w-3 h-3" />
          {t('contentHub.onScreens', '{{count}} screens', { count: group.total_count })}
        </span>
        <span className="flex items-center gap-1.5">
          <span className={`inline-block w-1.5 h-1.5 rounded-full ${
            group.online_count > 0 ? 'bg-emerald-500' : 'bg-muted-foreground/40'
          }`} />
          <span className="text-muted-foreground">
            {group.online_count}/{group.total_count} {t('common.online', 'online')}
          </span>
        </span>
      </div>

      <div className="mt-2 h-1 rounded-full bg-muted overflow-hidden">
        <div className="h-full bg-emerald-500 transition-all" style={{ width: `${ratio}%` }} />
      </div>

      <Chevron className="absolute top-1/2 -translate-y-1/2 end-3 w-4 h-4 text-muted-foreground/0 group-hover:text-muted-foreground transition-colors" />
    </div>
  )
}

// Empty state — fills a tall area so it reads as centered rather than clipped.
function GroupsEmptyState({ onCreate, hasSearch }: { onCreate: () => void; hasSearch: boolean }) {
  const { t } = useTranslation()
  if (hasSearch) {
    return (
      <div className="flex items-center justify-center text-center min-h-[40vh] text-muted-foreground">
        <p className="text-sm">{t('common.noData', 'No data found')}</p>
      </div>
    )
  }
  return (
    <div className="flex flex-col items-center justify-center text-center min-h-[45vh] gap-3">
      {/* Stacked-cards illustration mirroring the design reference */}
      <div className="relative w-44 h-32">
        <div className="absolute inset-0 rounded-full bg-muted/40" />
        <div className="absolute top-3 start-4 w-32 h-7 rounded-md bg-card border border-border shadow-sm flex items-center gap-2 px-2">
          <div className="w-4 h-4 rounded-sm bg-muted" />
          <div className="flex-1 h-1.5 rounded-full bg-muted" />
        </div>
        <div className="absolute top-12 start-10 w-32 h-7 rounded-md bg-card border border-border shadow-sm flex items-center gap-2 px-2">
          <div className="w-4 h-4 rounded-sm bg-muted" />
          <div className="flex-1 h-1.5 rounded-full bg-muted" />
        </div>
        <div className="absolute top-[5.25rem] start-4 w-32 h-7 rounded-md bg-card border border-border shadow-sm flex items-center gap-2 px-2">
          <div className="w-4 h-4 rounded-sm bg-muted" />
          <div className="flex-1 h-1.5 rounded-full bg-muted" />
        </div>
      </div>
      <h3 className="text-base font-semibold text-foreground">
        {t('dashboard.groupsEmptyTitle', "You haven't created any groups yet")}
      </h3>
      <p className="text-sm text-muted-foreground max-w-md">
        {t('dashboard.groupsEmptyDesc', 'Organize your screens into groups for easier access and more efficient management. Click the "New Group" button to get started now!')}
      </p>
      <Button onClick={onCreate} className="gap-2 mt-2">
        <Plus className="w-4 h-4" />
        {t('dashboard.newGroup', 'New Group')}
      </Button>
    </div>
  )
}
